import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatDate, formatNumber } from '../../lib/format.ts';
import { useLocale } from '../../lib/i18n.tsx';
import { colorFor } from '../palette.ts';
import { ChartTable } from './ChartTable.tsx';

export interface TrendPoint {
  bucket: string;
  value: number;
}

export interface TrendSeriesData {
  /** Stable key (a source id, a metric name): the same key keeps its colour in every chart. */
  key: string | null;
  label: string;
  points: readonly TrendPoint[];
}

/** `2026-10-21` → `21.10` (axis labels stay short). */
const axisLabel = (bucket: string): string => formatDate(bucket).slice(0, 5);

/**
 * Bars over time, one series or stacked series (e.g. new leads per source). Animation is off and
 * the chart is keyboard-navigable; its data is also available as a table for assistive technology.
 */
export function TrendChart({
  title,
  series,
  stacked = false,
  height = 260,
  unitLabel,
}: {
  title: string;
  series: readonly TrendSeriesData[];
  stacked?: boolean;
  height?: number;
  unitLabel?: string;
}) {
  const locale = useLocale();
  const buckets = series[0]?.points.map((p) => p.bucket) ?? [];
  const data = buckets.map((bucket, index) => {
    const row: Record<string, number | string> = { bucket };
    series.forEach((s, i) => {
      row[`s${i}`] = s.points[index]?.value ?? 0;
    });
    return row;
  });

  return (
    <figure aria-label={title} className="w-full">
      <div style={{ height }} className="w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
            accessibilityLayer
          >
            <CartesianGrid vertical={false} stroke="var(--border)" />
            <XAxis
              dataKey="bucket"
              tickFormatter={axisLabel}
              tick={{ fontSize: 12, fill: 'var(--muted-foreground)' }}
              minTickGap={16}
            />
            <YAxis
              allowDecimals={false}
              width={44}
              tick={{ fontSize: 12, fill: 'var(--muted-foreground)' }}
              tickFormatter={(value: number) => formatNumber(value, locale)}
            />
            <Tooltip
              isAnimationActive={false}
              labelFormatter={(bucket) => (typeof bucket === 'string' ? formatDate(bucket) : '')}
              formatter={(value, name) => [
                `${formatNumber(Number(value), locale)}${unitLabel ? ` ${unitLabel}` : ''}`,
                name,
              ]}
            />
            {series.length > 1 ? <Legend /> : null}
            {series.map((s, i) => (
              <Bar
                key={s.key ?? 'none'}
                dataKey={`s${i}`}
                name={s.label}
                fill={colorFor(s.key)}
                {...(stacked ? { stackId: 'stack' } : {})}
                isAnimationActive={false}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
      <ChartTable
        title={title}
        columns={['', ...series.map((s) => s.label)]}
        rows={buckets.map((bucket, index) => [
          formatDate(bucket),
          ...series.map((s) => formatNumber(s.points[index]?.value ?? 0, locale)),
        ])}
      />
    </figure>
  );
}
